import { useCallback, useEffect, useRef } from 'react'
import { useBeforeUnload, useBlocker } from 'react-router'
import type { FormState } from 'react-hook-form'
import { useConfirmationDialog, ConfirmationDialogResult } from '../ConfirmDialog'

const CONFIRM_PROMPT = 'You have some unsaved data, do you really want to leave the page?'

interface SafeRoutingProps {
  enabled?: boolean
  formState?: FormState<any>
}

/**
 * Component to prompt confirmation before leaving form page
 * @param enabled - if true, prompt will be shown
 * @param formState - react-hook-form form state, checks if form is dirty and submit was not successful
 */
const SafeRouting = ({ enabled, formState }: SafeRoutingProps) => {
  // Don't use formState.isDirty as it is always true since we use temporary fields, e.g _applianceSection, etc
  const anyDirtyFields = (formState: FormState<any>) => Object.values(formState.dirtyFields).some((isDirty) => isDirty)
  const isRHFDirty = formState
    ? !formState.isSubmitting && anyDirtyFields(formState) && !formState.isSubmitSuccessful
    : false
  const isDirty = enabled || isRHFDirty

  // For closing tab or refreshing page
  useBeforeUnload(
    useCallback(
      (e) => {
        if (!isDirty) return
        e.preventDefault()
        e.returnValue = '' // CONFIRM_PROMPT is ignored by most browsers
      },
      [isDirty],
    ),
    { capture: true },
  )

  // For navigating within the app
  const { showConfirmationAsync } = useConfirmationDialog()
  const blocker = useBlocker(isDirty)
  const prevBlockerState = useRef(blocker.state)

  useEffect(() => {
    if (blocker.state === 'blocked' && prevBlockerState.current !== 'blocked') {
      prevBlockerState.current = 'blocked'
      showConfirmationAsync(CONFIRM_PROMPT).then((result) => {
        if (result === ConfirmationDialogResult.OK) {
          blocker.proceed?.()
        } else {
          blocker.reset?.()
        }
      })
    } else if (blocker.state !== 'blocked') {
      prevBlockerState.current = blocker.state
    }
  }, [blocker, showConfirmationAsync])

  return null
}

export default SafeRouting
